import React, { useState } from 'react';
import './BragBarLogosManagement.css';
import ImageContainer from './ImageContainer';
import ImageTooBigPopup from './ImageTooBigPopup';

function BragBarLogosManagement({
    logos,
    addLogo,
    removeLogo,
    updateLogo,
    uploadImage,
    editData,
    handleInputChange
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    
    // Upload States für Image-Upload-System
    const [uploadingStates, setUploadingStates] = useState({});
    const [showImageTooBigPopup, setShowImageTooBigPopup] = useState(false);
    const [tooBigFileInfo, setTooBigFileInfo] = useState({ size: 0, name: '' });

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    const setUploadingState = (logoId, isUploading) => {
        setUploadingStates(prev => ({
            ...prev,
            [logoId]: isUploading
        }));
    };

    // Image Upload Handler mit Modal-Integration
    const handleImageUpload = async (logoId, file) => {
        if (!file) return;
        
        // File-Size-Validation mit Modal
        if (file.size > 2 * 1024 * 1024) {
            setTooBigFileInfo({ size: file.size, name: file.name });
            setShowImageTooBigPopup(true);
            return;
        }
        
        setUploadingState(logoId, true);
        
        try {
            const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
            const { getAuth } = await import('firebase/auth');
            
            const storage = getStorage();
            const auth = getAuth();
            const userId = auth.currentUser.uid;
            const fieldName = `logo_${logoId}_image`;
            const imageRef = storageRef(storage, `pages/${userId}/${editData.widgetId || 'widget'}/${fieldName}_${file.name}`);
            const uploadTask = uploadBytesResumable(imageRef, file);

            uploadTask.on('state_changed', 
                (snapshot) => {
                    // Upload progress
                }, 
                (error) => {
                    console.error('❌ Upload error:', error);
                    alert('Upload failed. Please try again.');
                    setUploadingState(logoId, false);
                }, 
                () => {
                    getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                        updateLogo(logoId, 'image', downloadURL);
                        setUploadingState(logoId, false);
                        console.log('✅ Logo uploaded:', downloadURL);
                    });
                }
            );
        } catch (error) {
            console.error('❌ Firebase import error:', error);
            alert('Upload failed. Please try again.');
            setUploadingState(logoId, false);
        }
    };

    return (
        <div className="brag-bar-logos-management">
            {/* Toggle Header - 1:1 Text-Edit Design */}
            <div 
                className={`brag-bar-logos-header ${isExpanded ? 'expanded' : ''}`}
                onClick={toggleExpanded}
            >
                <div className="brag-bar-logos-header-content">
                    <div className="brag-bar-logos-header-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <rect x="2" y="3" width="4" height="3" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                            <rect x="10" y="3" width="4" height="3" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                            <rect x="6" y="10" width="4" height="3" rx="1" stroke="currentColor" strokeWidth="1.2"/>
                            <circle cx="8" cy="8" r="1" fill="currentColor"/>
                        </svg>
                    </div>
                    <h3 className="brag-bar-logos-header-title">Brand Logos</h3>
                    <div className="brag-bar-logos-counter">
                        {logos.length}
                    </div>
                </div>
                <div className={`brag-bar-logos-toggle-arrow ${isExpanded ? 'expanded' : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path 
                            d="M4 6L8 10L12 6" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                        />
                    </svg>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div className="brag-bar-logos-content">
                    {/* Add Logo Section */}
                    <div className="add-logo-section">
                        <button onClick={addLogo} className="add-logo-btn">
                            <div className="btn-icon">
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                </svg>
                            </div>
                            <span>Add Brand Logo</span>
                        </button>
                    </div>

                    {/* Logos Grid */}
                    {logos.length > 0 && (
                        <div className="logos-grid">
                            {logos.map((logo, index) => (
                                <div key={logo.id} className="logo-edit-card">
                                    {/* Logo Header */}
                                    <div className="logo-card-header">
                                        <div className="logo-card-title">
                                            <span className="logo-number">#{index + 1}</span>
                                            <span className="logo-label">Brand Logo</span>
                                        </div>
                                        <button
                                            onClick={() => removeLogo(logo.id)}
                                            className="logo-remove-btn"
                                            title="Remove logo"
                                        >
                                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Logo Image Upload */}
                                    <div className="logo-image-section">
                                        <div className="logo-image-preview">
                                            {logo.image ? (
                                                <img 
                                                    src={logo.image} 
                                                    alt={logo.alt || 'Brand Logo'} 
                                                    className="logo-preview-img"
                                                />
                                            ) : (
                                                <div className="logo-placeholder">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                        <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                                                        <circle cx="8.5" cy="10.5" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                                                        <path d="M21 15L16 10L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </div>
                                            )}
                                        </div>

                                        {/* Image Controls */}
                                        <div className="logo-image-controls">
                                            <label className="logo-image-control-btn upload" title="Upload logo">
                                                {uploadingStates[logo.id] ? (
                                                    <div className="logo-upload-spinner"></div>
                                                ) : (
                                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                        <path d="M7 1V9M4 4L7 1L10 4M2 9V11C2 12.1 2.9 13 4 13H10C11.1 13 12 12.1 12 11V9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                )}
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        if (e.target.files[0]) {
                                                            handleImageUpload(logo.id, e.target.files[0]);
                                                        }
                                                    }}
                                                    style={{ display: 'none' }}
                                                />
                                            </label>

                                            {logo.image && (
                                                <button
                                                    onClick={() => updateLogo(logo.id, 'image', '')}
                                                    className="logo-image-control-btn clear"
                                                    title="Clear image"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                        <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Logo Settings */}
                                    <div className="logo-settings">
                                        <div className="logo-input-group">
                                            <label className="logo-input-label">Alt Text</label>
                                            <input
                                                type="text"
                                                value={logo.alt || ''}
                                                onChange={(e) => updateLogo(logo.id, 'alt', e.target.value)}
                                                placeholder="Brand name..."
                                                className="logo-input"
                                            />
                                        </div>

                                        <div className="logo-input-group">
                                            <label className="logo-input-label">Link (optional)</label>
                                            <input
                                                type="url"
                                                value={logo.link || ''}
                                                onChange={(e) => updateLogo(logo.id, 'link', e.target.value)}
                                                placeholder="https://brand.com"
                                                className="logo-input"
                                            />
                                        </div>

                                        <div className="logo-size-controls">
                                            <div className="logo-input-group">
                                                <label className="logo-input-label">Width</label>
                                                <input
                                                    type="number"
                                                    value={logo.width || ''}
                                                    onChange={(e) => updateLogo(logo.id, 'width', e.target.value)}
                                                    placeholder="Auto"
                                                    className="logo-input logo-size-input"
                                                />
                                            </div>
                                            <div className="logo-input-group">
                                                <label className="logo-input-label">Height</label>
                                                <input
                                                    type="number"
                                                    value={logo.height || ''}
                                                    onChange={(e) => updateLogo(logo.id, 'height', e.target.value)}
                                                    placeholder="Auto"
                                                    className="logo-input logo-size-input"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {logos.length === 0 && (
                        <div className="empty-state">
                            <div className="empty-state-icon">
                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                    <rect x="6" y="12" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                                    <rect x="30" y="12" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                                    <rect x="18" y="27" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                                    <circle cx="24" cy="24" r="2" fill="currentColor" fillOpacity="0.3"/>
                                </svg>
                            </div>
                            <h4 className="empty-state-title">No Brand Logos Yet</h4>
                            <p className="empty-state-description">Click "Add Brand Logo" to showcase trusted brands</p>
                        </div>
                    )}
                </div>
            )}

            {/* Image Too Big Popup */}
            <ImageTooBigPopup 
                isOpen={showImageTooBigPopup}
                onClose={() => setShowImageTooBigPopup(false)}
                fileSize={tooBigFileInfo.size}
                fileName={tooBigFileInfo.name}
                maxSize={2}
            />
        </div>
    );
}

export default BragBarLogosManagement;
